import coefficients from "./cdc-ivf-coefficients.json";

/** Mathematical model inputs, separate from API patient records and clinic rates. */
export type CDCIVFDiagnosis =
  | "tubal"
  | "male_factor"
  | "endometriosis"
  | "pco"
  | "diminished_ovarian_reserve"
  | "uterine"
  | "other"
  | "unexplained";
export type CDCIVFInput = {
  age: number;
  bmi: number;
  population: "new_own_eggs" | "returning_own_eggs" | "new_donor_eggs";
  pregnancies: 0 | 1 | 2;
  liveBirths: 0 | 1 | 2;
  /** null selects CDC's separately fitted model without diagnosis predictors. */
  diagnoses: readonly CDCIVFDiagnosis[] | null;
};

const diagnosisFields = {
  tubal: "Tubal",
  male_factor: "Male factor",
  endometriosis: "Endometriosis",
  pco: "PCO",
  diminished_ovarian_reserve: "Diminished ovarian reserve",
  uterine: "Uterine",
  other: "Other",
  unexplained: "Unexplained",
} as const;

function coefficientAt(values: readonly number[], index: number): number {
  const value = values[index];
  if (value === undefined || !Number.isFinite(value)) {
    throw new Error("Incomplete CDC model coefficients");
  }
  return value;
}

/** CDC's published national cumulative model; no clinic rate or transported OR. */
export function estimateCDCIVF(input: CDCIVFInput) {
  const returning = input.population === "returning_own_eggs";
  const minAge = returning ? 24 : 20;
  const maxAge = returning ? 48 : 50;
  if (
    !Number.isInteger(input.age) ||
    input.age < minAge ||
    input.age > maxAge ||
    !Number.isFinite(input.bmi) ||
    input.bmi <= 0 ||
    ![0, 1, 2].includes(input.pregnancies) ||
    ![0, 1, 2].includes(input.liveBirths) ||
    input.liveBirths > input.pregnancies ||
    (input.diagnoses?.includes("unexplained") && input.diagnoses.length > 1)
  ) {
    return null;
  }
  const known = input.diagnoses !== null;
  const id =
    input.population === "new_donor_eggs"
      ? known
        ? "f11-13"
        : "f14-16"
      : returning
        ? known
          ? "f7-8"
          : "f9-10"
        : known
          ? "f1-3"
          : "f4-6";
  const model = coefficients.models[id];
  // CDC rounds BMI to one decimal and bounds its model covariate at 17–45.
  const bmi = Math.min(45, Math.max(17, Math.round(input.bmi * 10) / 10));
  const bucket = (n: 0 | 1 | 2) => (n === 2 ? "2+" : n === 1 ? "1" : "0");
  let cumulative = 0;
  const probabilities = model.Intercept.map((intercept, i) => {
    let logOdds =
      intercept +
      coefficientAt(model["Patient Age"], i) * input.age +
      coefficientAt(model["Age Power Coefficient"], i) *
        input.age ** coefficientAt(model["Age Power Factor"], i) +
      coefficientAt(model.BMI, i) * bmi +
      coefficientAt(model["BMI Power Coefficient"], i) *
        bmi ** coefficientAt(model["BMI Power Factor"], i) +
      coefficientAt(model.Gravida[bucket(input.pregnancies)], i) +
      coefficientAt(model["Previous live births"][bucket(input.liveBirths)], i);
    if (known && "Tubal" in model) {
      for (const diagnosis of new Set(input.diagnoses)) {
        logOdds += coefficientAt(model[diagnosisFields[diagnosis]].Yes, i);
      }
    }
    const probability = 1 / (1 + Math.exp(-logOdds));
    cumulative += (1 - cumulative) * probability;
    return cumulative;
  });
  return {
    probabilities,
    bmiUsed: bmi,
    modelId: id,
    unit: input.population === "new_donor_eggs" ? "transfer" : "retrieval",
    sourceUrl: coefficients.sourceUrl,
  };
}
